import { useMutation, type UseMutationOptions } from '@tanstack/react-query';

import { REQUIRED_IDN_ROLES } from '~/components/forms/adminUser/validations';
import { authState } from '~/features/auth/state';

import { MUTATION_KEY } from './keys';
import { clmApiClient, clmUserApiClient } from '../clients';
import { REAL_DEPARTMENT_ID, REAL_IDN_ID } from '../msw/constants';
import type {
  AdminUser,
  AdminUserCreateRequest,
  AdminUserCreateRequestBody,
  ApiErrorResponse,
} from '../types';
import { isApiErrorResponse } from '../utils';
/**
 * NOTE:
 * - Currently still uses both the real API and a mocked API until the backend is stable.
 * - Hardcoded IDs are used as temporary placeholders because the backend does not yet provide them consistently.
 */
const createAdminUser = async (
  data: AdminUserCreateRequest,
): Promise<AdminUser> => {
  const userIdnId = authState.getIDNId();
  const idnId = data.idnId ?? userIdnId;
  // Validate that roles requiring an IDN ID actually have one

  if (!idnId && REQUIRED_IDN_ROLES.includes(data.role)) {
    throw new Error('IDN ID is required to create an admin user.');
  }
  // -------------------------------
  // REAL API LOGIC (Temporary Setup)
  // -------------------------------

  // We are wrapping the real API call in a try-catch to ensure that it does not block
  // the mocked API logic in case of failure, as the real backend is not yet stable.
  try {
    const response = await clmUserApiClient.post<AdminUser, ApiErrorResponse>(
      'user',
      {
        user: {
          ...data,
          // DON'T REMOVE: Keep until we removed all mocked data
          departmentId: REAL_DEPARTMENT_ID,
          // DON'T REMOVE: Keep until we removed all mocked data
          // Using hardcoded values until backend provides a consistent mechanism
          // to retrieve IDN IDs and Department IDs from real data sources.
          idnId: userIdnId ?? REAL_IDN_ID,
          // TODO: Replace with proper user metadata setup as per System Engineering guidelines
          country: 'us',
          language: 'en-US',
        },
      } as AdminUserCreateRequestBody,
    );

    if (!response.ok) {
      if (isApiErrorResponse(response.data)) {
        throw new Error(response.data.details);
      }
      throw new Error('Creating admin user via real API failed');
    }
  } catch (e) {
    console.error(e);
  }

  // -------------------------------
  // MOCKED API LOGIC (Fallback)
  // -------------------------------
  // This is still in place because the real backend cannot yet
  // reliably return existing IDN and Department data.

  const responseMockedAPI = await clmApiClient.post<
    AdminUser,
    ApiErrorResponse
  >('admin/users', {
    user: {
      ...data,
      idnId,
    },
  } as AdminUserCreateRequestBody);

  if (!responseMockedAPI.data) {
    throw new Error('No data returned from server');
  }

  if (!responseMockedAPI.ok) {
    if (isApiErrorResponse(responseMockedAPI.data)) {
      throw new Error(responseMockedAPI.data.details);
    }

    throw new Error('Unknown error');
  }

  return responseMockedAPI.data;
};

export const useAdminUserCreateMutation = (
  config?: Omit<
    UseMutationOptions<AdminUser, Error, AdminUserCreateRequest>,
    'mutationFn'
  >,
) =>
  useMutation({
    mutationKey: [MUTATION_KEY.USER_CREATE],
    mutationFn: createAdminUser,
    ...config,
  });
